
import { FC, useEffect, useState } from 'react';
import { Form, Modal } from 'antd';
import { isArray, refactorSelection } from '@lhb/func';
import { useMethods } from '@lhb/hook';
import { useSelector } from 'react-redux';
import { shopStatusList, updateShopStatus } from '@/common/api/expandStore/shop';
import { StoreStatus } from '@/common/components/business/ExpandStore/ts-config';
import styles from './index.module.less';
import dayjs from 'dayjs';
import ApprovalCreateDialog from '@/common/components/business/ExpandStore/ApprovalCreateDialog';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2Message from '@/common/components/Others/V2Hint/V2Message';

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 },
};
/**
 * @Description 门店详情-变更状态弹框
 */
const ChangeStatusModal: FC<{
  visible,
  setVisible,
  record,
  onRefresh,
}> = ({
  visible,
  setVisible,
  record,
  onRefresh,
}) => {
  const [form] = Form.useForm();
  const [statuses, setStatuses] = useState<any[]>([]);
  const [isLock, setIsLock] = useState<boolean>(false);
  const [approvalVisible, setApprovalVisible] = useState<boolean>(false);
  const [approvalParams, setApprovalParams] = useState<any>({});
  const status = Form.useWatch('status', form);
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);
  const isSjtb = !!tenantCheckData?.isSjtb;

  const methods = useMethods({
    /**
     * @description 获取下拉选项（与当前门店状态相关）
     */
    async getSelection() {
      const data = await shopStatusList({ id: record.id });
      isArray(data) && setStatuses(refactorSelection(data));
    },

    onCancel() {
      setApprovalVisible(false);
      setVisible(false);
    },

    onSuccess() {
      V2Message.success('变更成功');
      setVisible(false);
      onRefresh && onRefresh();
    },

    /**
     * @description 门店状态变更审批提交成功
     */
    handleApprovalComplete() {
      setVisible(false);
      onRefresh && onRefresh();
    },

    onSubmit() {
      form.validateFields().then((values: any) => {
        // 日期参数处理
        const houseDeliveryDate = values.houseDeliveryDate ? dayjs(values.houseDeliveryDate).format('YYYY-MM-DD') : undefined;
        const openDate = values.openDate ? dayjs(values.openDate).format('YYYY-MM-DD') : undefined;
        const closeDate = values.closeDate ? dayjs(values.closeDate).format('YYYY-MM-DD') : undefined;

        const params: any = {
          id: record.id,
          status: values.status,
          houseDeliveryDate,
          openDate,
          closeDate,
          closeReason: values.closeReason || undefined,
        };

        // 已配置审批流且门店状态发生变更时，先发起审批。
        if (record?.statusChangeApproval?.need && record?.status !== values.status) {
          params.storeStatusChangeRequest = {
            ...params
          };
          params.type = record?.statusChangeApproval?.type;
          params.typeValue = record?.statusChangeApproval?.typeValue;
          setApprovalParams(params);
          setApprovalVisible(true);
          return;
        }

        if (isLock) return;
        setIsLock(true);
        updateShopStatus(params).then(() => {
          methods.onSuccess();
        }).finally(() => {
          setIsLock(false);
        });
      });
    },
  });

  useEffect(() => {
    if (visible) {
      const statusNameField = record?.configs?.find(item => item.field === 'status');
      const result = statusNameField.selectionList.find(item => item.value === record[statusNameField.field]);

      form.setFieldValue('currentStatus', result?.name || '-');
      methods.getSelection();
    } else {
      form.resetFields();
    }
  }, [visible]);

  return (
    <Modal title='变更状态' open={visible} width={480} onOk={methods.onSubmit} onCancel={methods.onCancel} className={styles.changeStatus}>
      <Form {...layout} form={form}>
        <V2FormInput
          label='当前状态'
          name='currentStatus'
          disabled
        />
        <V2FormSelect
          label='变更状态'
          name='status'
          options={statuses}
          required/>
        { status === StoreStatus.HouseDelivered ? <V2FormDatePicker
          label='交房日期'
          name='houseDeliveryDate'
          required/> : <></>}
        { !isSjtb && status === StoreStatus.Opened ? <V2FormDatePicker
          label='开业日期'
          name='openDate'
          required/> : <></>}
        { status === StoreStatus.Closed ? <>
          {
            !isSjtb && <V2FormDatePicker
              label='闭店时间'
              name='closeDate'
              required/>
          }
          <V2FormTextArea label='闭店原因' name='closeReason' maxLength={500}/>
        </> : <></>}
      </Form>
      <ApprovalCreateDialog
        visible={approvalVisible}
        setVisible={setApprovalVisible}
        id={record.id}
        type={approvalParams.type}
        typeValue={approvalParams.typeValue}
        specialParams={approvalParams}
        onComplete={methods.handleApprovalComplete}
      />
    </Modal>
  );
};

export default ChangeStatusModal;
